/**
 * Hook for LOC health monitoring.
 * Calculates current collateral ratio vs liquidation threshold for each active LOC.
 */

import { useMemo } from 'react'
import { LOCEvent } from './useLOCEvents'
import { formatUnits } from 'viem'
import { SUPPORTED_TOKENS } from '@contracts/tokens'

export type HealthLevel = 'healthy' | 'warning' | 'danger' | 'liquidatable'

export interface LOCHealth {
  locId: bigint
  collateralRatio: number         // current collateral ratio (e.g. 1.8 = 180%)
  maintenanceFactor: number       // minimum required ratio (e.g. 1.2 = 120%)
  healthPercent: number           // how healthy (100% = perfectly at maintenance, >100% is buffer)
  level: HealthLevel
  color: 'green' | 'yellow' | 'red' | 'gray'
  label: string
  collateralUSD: number
  creditedUSD: number
}

/**
 * Calculate health for a single LOC given current prices.
 */
function calculateLOCHealth(
  loc: LOCEvent,
  prices: Record<string, number>
): LOCHealth {
  // Find tokens by address
  const collateralToken = Object.values(SUPPORTED_TOKENS).find(
    t => t.address.toLowerCase() === loc.collateralTokenAddress.toLowerCase()
  )
  const creditedToken = Object.values(SUPPORTED_TOKENS).find(
    t => t.address.toLowerCase() === loc.creditedTokenAddress.toLowerCase()
  )

  const collateralDecimals = collateralToken?.decimals ?? 18
  const creditedDecimals = creditedToken?.decimals ?? 6
  const collateralSymbol = collateralToken?.symbol ?? 'WETH'
  const creditedSymbol = creditedToken?.symbol ?? 'USDC'

  const collateralAmount = parseFloat(
    formatUnits(loc.collateralTokenAmount, collateralDecimals)
  )
  const creditedAmount = parseFloat(
    formatUnits(loc.creditedTokenAmount, creditedDecimals)
  )

  const collateralPrice = prices[collateralSymbol] ?? 0
  const creditedPrice = prices[creditedSymbol] ?? 1

  const collateralUSD = collateralAmount * collateralPrice
  const creditedUSD = creditedAmount * creditedPrice

  // Collateral ratio = collateral value / credited value
  const collateralRatio = creditedUSD > 0 ? collateralUSD / creditedUSD : Infinity

  // Maintenance factor is the minimum collateral ratio (collateralUSD / creditedUSD).
  // collateralFactorBasisPoints is max LTV in bps (e.g. 6000 bps = 60% LTV → min ratio 10000/6000 = 1.667x).
  const maintenanceFactor = loc.collateralFactorBasisPoints > 0
    ? 10000 / loc.collateralFactorBasisPoints
    : 1.2 // default 120% collateral ratio

  // healthPercent: how much buffer above maintenance?
  // 100% = right at maintenance (about to be liquidated)
  // >100% = has buffer
  const healthPercent = maintenanceFactor > 0
    ? (collateralRatio / maintenanceFactor) * 100
    : 100

  let level: HealthLevel
  let color: 'green' | 'yellow' | 'red' | 'gray'
  let label: string

  if (collateralPrice === 0 || creditedPrice === 0) {
    level = 'healthy'
    color = 'gray'
    label = 'Unknown'
  } else if (healthPercent >= 150) {
    level = 'healthy'
    color = 'green'
    label = 'Healthy'
  } else if (healthPercent >= 120) {
    level = 'warning'
    color = 'yellow'
    label = 'At Risk'
  } else if (healthPercent >= 100) {
    level = 'danger'
    color = 'red'
    label = 'Danger'
  } else {
    level = 'liquidatable'
    color = 'red'
    label = 'Liquidatable'
  }

  return {
    locId: loc.id,
    collateralRatio,
    maintenanceFactor,
    healthPercent,
    level,
    color,
    label,
    collateralUSD,
    creditedUSD,
  }
}

/**
 * Hook: compute health for an array of LOC events given current prices.
 */
export function useLOCHealth(
  locs: LOCEvent[],
  prices: Record<string, number> | undefined
): LOCHealth[] {
  return useMemo(() => {
    if (!prices || locs.length === 0) return []

    return locs.map(loc => calculateLOCHealth(loc, prices))
  }, [locs, prices])
}

/**
 * Hook: compute health for a single LOC.
 */
export function useSingleLOCHealth(
  loc: LOCEvent | undefined,
  prices: Record<string, number> | undefined
): LOCHealth | null {
  return useMemo(() => {
    if (!loc || !prices) return null
    return calculateLOCHealth(loc, prices)
  }, [loc, prices])
}
